// API client.
//
// The tail deliberately uses fetch + ReadableStream rather than EventSource:
// EventSource cannot set request headers, so it could not send the read key
// without putting the secret in the query string (where it lands in access
// logs). The cost is ~30 lines of manual SSE parsing, below.

export type TraceLine = {
  id: string;
  ts: string;
  level: string;
  msg: string;
  trace_id?: string | null;
  conversation_id?: string | null;
  event?: string | null;
  turn?: number | null;
  speech_id?: string | null;
  replica?: string | null;
  pid?: number | null;
  [k: string]: unknown;
};

export type CallSummary = {
  conversationId: string;
  tenant: string;
  lineCount: number;
  expiresInSeconds: number;
};

const KEY_STORAGE = 'logger-tool.readKey';

export const getKey = (): string => localStorage.getItem(KEY_STORAGE) ?? '';
export const setKey = (k: string): void => localStorage.setItem(KEY_STORAGE, k);

function headers(): HeadersInit {
  const key = getKey();
  return key ? { 'x-trace-key': key } : {};
}

/** Error that carries the HTTP status, so callers can single out 401. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isUnauthorized = (e: unknown): boolean =>
  e instanceof ApiError && e.status === 401;

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: headers() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as Record<string, unknown>);
    throw new ApiError(
      (body as { error?: string }).error ?? `${res.status} ${res.statusText}`,
      res.status,
    );
  }
  return (await res.json()) as T;
}

export const fetchTenants = (): Promise<{ tenants: string[] }> => getJson('/api/tenants');

/** Which tenant(s) hold this call — lets the UI resolve it from the id alone. */
export const resolveCall = (
  conversationId: string,
): Promise<{ conversationId: string; tenants: string[] }> =>
  getJson(`/api/resolve/${encodeURIComponent(conversationId)}`);

export const fetchCalls = (
  tenant: string,
): Promise<{ calls: Array<{ conversationId: string; lastSeen: string }> }> =>
  getJson(`/api/calls?tenant=${encodeURIComponent(tenant)}`);

export const fetchSummary = (tenant: string, id: string): Promise<CallSummary> =>
  getJson(`/api/calls/${encodeURIComponent(id)}?tenant=${encodeURIComponent(tenant)}`);

export const fetchLogs = (
  tenant: string,
  id: string,
  afterId?: string,
): Promise<{ lines: TraceLine[]; cursor: string | null; hasMore: boolean }> =>
  getJson(
    `/api/calls/${encodeURIComponent(id)}/logs?tenant=${encodeURIComponent(tenant)}` +
      (afterId ? `&afterId=${encodeURIComponent(afterId)}` : ''),
  );

export type TailHandlers = {
  onBackfill: (lines: TraceLine[]) => void;
  onLine: (line: TraceLine) => void;
  onEnded: (reason: string) => void;
  onError: (message: string) => void;
};

/**
 * Open a live tail. Returns a function that stops it — call that on unmount or
 * the server keeps polling Redis for an abandoned tab until its 15min cap.
 */
export function openTail(
  tenant: string,
  id: string,
  afterId: string | undefined,
  handlers: TailHandlers,
): () => void {
  const controller = new AbortController();

  void (async () => {
    try {
      const url =
        `/api/calls/${encodeURIComponent(id)}/stream?tenant=${encodeURIComponent(tenant)}` +
        (afterId ? `&afterId=${encodeURIComponent(afterId)}` : '');
      const res = await fetch(url, { headers: headers(), signal: controller.signal });
      if (!res.ok || !res.body) {
        handlers.onError(`tail failed: ${res.status}`);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line.
        let split: number;
        while ((split = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, split);
          buffer = buffer.slice(split + 2);

          let event = 'message';
          const dataLines: string[] = [];
          for (const raw of frame.split('\n')) {
            if (raw.startsWith(':')) continue; // keepalive comment
            if (raw.startsWith('event:')) event = raw.slice(6).trim();
            else if (raw.startsWith('data:')) dataLines.push(raw.slice(5).trim());
          }
          if (!dataLines.length) continue;

          let payload: unknown;
          try {
            payload = JSON.parse(dataLines.join('\n'));
          } catch {
            continue;
          }

          if (event === 'backfill') {
            handlers.onBackfill((payload as { lines: TraceLine[] }).lines ?? []);
          } else if (event === 'line') {
            handlers.onLine(payload as TraceLine);
          } else if (event === 'ended') {
            handlers.onEnded((payload as { reason?: string }).reason ?? 'ended');
          } else if (event === 'error') {
            handlers.onError((payload as { message?: string }).message ?? 'stream error');
          }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        handlers.onError((e as Error).message ?? 'tail failed');
      }
    }
  })();

  return () => controller.abort();
}
